<template>
  <div>
    <Loader
      v-if="isLoading"
      class="pb-3"
    />
    <template v-else>
      <!-- StudentCharacteristic Select -->
      <MultiSelectComponent
        :id="`${ruleReference}-student-characteristic-select`"
        v-model="selectedStudentCharacteristic"
        class="mb-2 my-1"
        :max-selected-options="1"
        :label="trans('subscription.classification_config.selectors.student_characteristic.select_student_characteristic')"
        :type="'student-characteristic'"
        track-by="name"
        @input="changeStudentCharacteristic"
      />

      <!-- Values -->
      <div class="m-2 mt-3">
        <div class="py-1 ps-3 mb-0 fw-bold">
          {{ trans_choice('subscription.classification_config.selectors.student_characteristic.values',2) }}
        </div>
        <div class="px-3">
          <template v-for="(value, index) in rule.values">
            <div
              :key="index"
              class="d-flex align-items-center"
            >
              <input
                :value="value"
                class="flex-grow-1 form-control me-4 my-1"
                @change="updateValue(index, $event)"
              >
              <RuleSmallButton
                v-if="index !== 0"
                class="btn-danger p-2 small-button-width fw-bold mx-2"
                :title="'-'"
                @click.native="removeValue(index)"
              />
              <!-- place a div with invisible text to fix the input width -->
              <div
                v-else
                class="p-2 mx-2"
                style="color: rgba(0, 0, 0, 0); cursor: default"
              >
                -
              </div>
            </div>
          </template>
          <RuleSmallButton
            class="mx-3 mt-2 btn-primary p-2 small-button-width"
            :title="''"
            @click.native="addValue()"
          >
            <span class="fa fa-plus" />
          </RuleSmallButton>
        </div>
      </div>

      <!-- Operator select -->
      <div class="ms-4 py-1 m-2 condition-border">
        <div class="fw-bold my-1">
          {{ trans('core.validate') }}
        </div>

        <select
          :id="`operator-${ruleReference}`"
          v-model="selectedOperator"
          class="form-control select-style p-2 my-1 fw-bold"
        >
          <template v-for="(optionValue) in operatorValues">
            <option
              :key="optionValue"
              :value="optionValue"
              :data-selected="optionValue"
            >
              {{ trans(`subscription.validation_rules.rules.student_characteristics.${optionValue}`) }}
            </option>
          </template>
        </select>
      </div>

      <div
        v-if="['eq', 'gt', 'lt'].includes(rule.operator)"
        class="ms-4 py-1 m-2 condition-border"
      >
        <div class="fw-bold my-1">
          {{ trans('core.count') }}
        </div>

        <input
          v-model="selectedCount"
          type="number"
          min="0"
          class="flex-grow-1 form-control me-4 my-1"
        >
      </div>
    </template>
  </div>
</template>

<script>
import ruleMixin from '@/employees/components/Rule/utilities/mixins/ruleMixin'
import { FILTER_OPERATOR_TYPE, RULE_PROPERTY } from '@/constants'
import { mapState } from 'vuex'
import MultiSelectComponent from '@/employees/components/FormComponents/MultiSelectComponent'

export default {
  name: 'RuleStudentCharacteristic',
  components: { MultiSelectComponent },
  mixins: [ruleMixin],
  props: {
    ruleReference: {
      required: true,
      type: String,
    },
    rule: {
      required: true,
      type: Object,
    },
  },
  data () {
    return {
      isLoading: false,
      operatorValues: FILTER_OPERATOR_TYPE,

      selectedStudentCharacteristic: [],
      selectedOperator: 'or',
      selectedCount: 0,
    }
  },
  computed: {
    ...mapState({
      missingId (state) {
        return state.studentCharacteristics.studentCharacteristics[this.rule.student_characteristic] == null
      },
      studentCharacteristics (state) {
        if (state.studentCharacteristics.studentCharacteristics[this.rule.student_characteristic]) {
          const characteristic = state.studentCharacteristics.studentCharacteristics[this.rule.student_characteristic]
          return [{
            id: characteristic.id,
            name: characteristic.name,
          }]
        }

        return []
      },
    }),
  },

  watch: {
    selectedOperator: function (newValue) {
      if (this.rule.operator !== newValue) {
        this.rulePropertyValueChange(this.ruleReference, RULE_PROPERTY.operator, newValue)
      }
    },
    selectedCount: function (newValue) {
      if (this.rule.count !== newValue) {
        this.rulePropertyValueChange(this.ruleReference, RULE_PROPERTY.count, Number.parseInt(newValue))
      }
    },
  },

  mounted () {
    this.isLoading = true
    // set the studentCharacteristic
    if (this.rule.student_characteristic && this.missingId) {
      this.$store.dispatch('getStudentCharacteristics', {
        queryParams: {
          filter: {
            id: this.rule.student_characteristic,
          },
        },
      }).then(() => {
        this.selectedStudentCharacteristic = this.studentCharacteristics
      })
    }

    if (this.rule.student_characteristic != null && !this.missingId) {
      this.selectedStudentCharacteristic = this.studentCharacteristics
    }

    // set the operator
    this.selectedOperator = this.rule.operator
    this.selectedCount = this.rule.count ?? 0

    this.isLoading = false
  },
  methods: {
    changeStudentCharacteristic (newValue) {
      if (this.selectedStudentCharacteristic.length === 0) {
        this.rulePropertyValueChange(this.ruleReference, RULE_PROPERTY.student_characteristic, [])
        return
      }

      const id = newValue[0].id
      this.rulePropertyValueChange(this.ruleReference, RULE_PROPERTY.student_characteristic, id)
    },

    addValue () {
      const newValues = this.rule.values
      newValues.push('')
      this.rulePropertyValueChange(this.ruleReference, RULE_PROPERTY.values, newValues)
    },
    updateValue (index, event) {
      const newValues = this.rule.values
      newValues[index] = event.target.value
      this.rulePropertyValueChange(this.ruleReference, RULE_PROPERTY.values, newValues)
    },
    removeValue (index) {
      const newValues = this.rule.values
      newValues.splice(index, 1)
      this.rulePropertyValueChange(this.ruleReference, RULE_PROPERTY.values, newValues)
    },
  },
}
</script>

<style lang="scss" scoped>
/deep/
.form-control {
  width: auto;
}
/deep/
.col-sm-2 {
  float: none;
}
/deep/
.col-sm-10 {
  float: none;
}
</style>
